import { customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT_APPS_TABLE } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsTableWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/dataTable';
import { getTableColumns } from './grid-config';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { validateDuplicates } from './utils/validators';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import {
  FullPagePopupConfigs,
  GzPolicyFullPageWindow,
} from '../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/GzPolicyFullPageWindow';
import { GzAntimalwareAntiExploitAppFormWebComponent } from './rulesItemForm';
import { AntimalwareAntiExploitUiApplication } from '../../../../../utils/uiSettings/antimalware/antimalware.types';
import { html, TemplateResult } from 'lit';
import { ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { getAntimalwareAntiExploitDetectionTechniquesDefaultValue } from '../../../../../utils/uiSettings/antimalware/antimalware.values';
import { AntimalwareAntiExploitApplicationStatus } from '@nirvana/shared/dtos/types/policies/common';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT_APPS_TABLE)
export class GzAntimalwareAntiExploitApplicationsTableWebComponent extends GzPolicyUiSettingsTableWebComponent<
  AntimalwareAntiExploitUiApplication,
  AntimalwareAntiExploitUiApplication
> {
  protected hasReorderRowAction = false;
  protected hasEditRowAction = false;
  protected columns = getTableColumns.call(
    this,
    this.hasAttribute('predefined'),
    !this.hasAttribute('predefined'),
    this.onApplicationNameClick.bind(this)
  );

  public resetValues() {
    const defaultDetectionTechniques = getAntimalwareAntiExploitDetectionTechniquesDefaultValue(
      !this.hasAttribute('predefined')
    );

    this.gridComponent.items = this.gridComponent.items.map(application => {
      return {
        ...application,
        status: AntimalwareAntiExploitApplicationStatus.DEFAULT,
        exploitDetectionTechniques: defaultDetectionTechniques,
      };
    });
  }

  protected renderHeaderButtonsOutsideGrid(): TemplateResult {
    // if predefined, do not show add button
    if (this.hasAttribute('predefined')) {
      return html``;
    }

    return html`
      <gz-button id="add-button" type="${ButtonTypes.OUTLINE}" @click="${this.addApplication.bind(this)}"
        >${translateFn('Add a Windows application')}</gz-button
      >
    `;
  }

  private addApplication() {
    const addRuleCallback = (formValue: AntimalwareAntiExploitUiApplication): ValidationResult => {
      // validate
      const validationResult = this.validateGridItemBeforeAdd(formValue);
      if (!validationResult.valid) {
        return validationResult;
      }

      // add item
      const cloneItems = [...this.gridComponent.getFormValue()];
      cloneItems.push(formValue);
      this.gridComponent.items = cloneItems;

      return validationResult;
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzAntimalwareAntiExploitAppFormWebComponent(true),
      strings: {
        title: translateFn('Add a Windows application'),
        confirm: translateFn('Add'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  private onApplicationNameClick(rowIndex: number) {
    const item: AntimalwareAntiExploitUiApplication = this.gridComponent.getFormValue()[rowIndex];
    const addRuleCallback = (formValue: AntimalwareAntiExploitUiApplication): ValidationResult => {
      // validate
      const validationResult = this.validateGridItemBeforeEdit(item, formValue);
      if (!validationResult.valid) {
        return validationResult;
      }

      // update item
      const cloneItems = [...this.gridComponent.getFormValue()];
      cloneItems[rowIndex] = formValue;
      this.gridComponent.items = cloneItems;

      return validationResult;
    };

    // create configs
    const configs: FullPagePopupConfigs = {
      bodyForm: new GzAntimalwareAntiExploitAppFormWebComponent(!this.hasAttribute('predefined'), item),
      strings: {
        title: translateFn('Edit Windows application'),
        confirm: translateFn('Save#preserve state'),
        cancel: translateFn('Cancel'),
        cancelConfirmation: {
          title: translateFn('Discard changes?'),
          body: translateFn('Changes you have made will be lost.'),
          cancel: translateFn('Keep changes'),
          confirm: translateFn('Discard'),
        },
      },
      confirmCallbackFn: addRuleCallback,
      shouldShowCancelConfirmation: true,
    };

    // show window
    const window = new GzPolicyFullPageWindow(configs);
    window.show();
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: AntimalwareAntiExploitUiApplication): ValidationResult {
    return validateDuplicates(gridItemFormValue, this.gridComponent.getFormValue());
  }

  protected validateGridItemBeforeEdit(
    oldItem: AntimalwareAntiExploitUiApplication,
    newItem: AntimalwareAntiExploitUiApplication
  ): ValidationResult {
    if (oldItem.applicationName === newItem.applicationName) {
      return { valid: true };
    }

    return validateDuplicates(newItem, this.gridComponent.getFormValue());
  }

  protected focusFirstInput() {
    this.addApplication();
  }
}
